import { EmployeeListItem } from "@/components/employee/EmployeeListItem.tsx";
import { useAppFilter } from "@/components/filter/use-filter.hook.tsx";
import { Search } from "@/components/search/Search.tsx";
import { useDebounceUpdate } from "@/hooks/use-debounce-update.jook.ts";
import { PreviewEmployeesModal } from "@/pages/employees/employee/widgets/PreviewEmployeesModal.tsx";
import {
  stripEmptyArrays,
  usePolicyReportFilters,
} from "@/pages/settings/time-off/index/time-off-policy-report.filters.ts";
import { TimeOffPolicyChecksModal } from "@/pages/time-off/widgets/TimeOffPolicyChecksModal.tsx";
import { useTimeOffAnalyticsPolicyReport } from "@/queries/analytics/use-time-off-analytics.query.ts";
import analyticsService, {
  analyticEvents,
  analyticProperties,
} from "@/services/analytics-service.ts";
import {
  ApiAnalyticsTimeOffLeavePolicyFilter,
  ApiAnalyticsTimeOffLeavePolicyReportDataRow,
} from "@/types/analytics/analytics.time-off.types.ts";
import { AnalyticReportTypes } from "@/types/analytics/analytics.types.ts";
import { ColumnSizes } from "@/types/common.types.ts";
import {
  Button,
  EmptyStateBase,
  Page,
  PaginationBlock,
  Stack,
  Table,
  Text,
  Tooltip,
  TooltipContent,
  TooltipTrigger,
  useModals,
} from "@skailer-com/uikit";
import { ColumnDef, createColumnHelper, DisplayColumnDef } from "@tanstack/react-table";
import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useEffectOnce } from "react-use";

export function TimeOffSettingsTabPolicyReport() {
  const { t } = useTranslation();
  const [, setSearchParams] = useSearchParams();
  const { openModal } = useModals();

  const {
    filter: { filter, typedFilters },
    isFilterReady,
  } = useAppFilter<ApiAnalyticsTimeOffLeavePolicyFilter>({
    filters: usePolicyReportFilters(),
    source: AnalyticReportTypes.timeOffPolicy,
  });
  const [searchTerm, setSearchTerm] = useState<string | undefined>(typedFilters.search);

  useDebounceUpdate({
    data: searchTerm,
    timeout: 300,
    execute: (newData) => {
      setSearchParams((searchParams) => {
        if (newData) searchParams.set("search", newData);
        else searchParams.delete("search");
        searchParams.set("page", "1");
        return searchParams;
      });
    },
  });

  const requestFilter = useMemo(() => stripEmptyArrays(filter), [filter]);

  const { data: report, isPending } = useTimeOffAnalyticsPolicyReport({
    filter: requestFilter,
    enabled: isFilterReady,
  });

  useEffectOnce(() => {
    analyticsService.trackEvent(analyticEvents.reporting.viewed, {
      [analyticProperties.title]: AnalyticReportTypes.timeOffPolicy,
    });
  });

  const columnHelper = createColumnHelper<ApiAnalyticsTimeOffLeavePolicyReportDataRow>();
  const columns = useMemo<ColumnDef<ApiAnalyticsTimeOffLeavePolicyReportDataRow>[]>(() => {
    const inner: DisplayColumnDef<ApiAnalyticsTimeOffLeavePolicyReportDataRow, unknown>[] = [];
    inner.push(
      columnHelper.display({
        id: "full_name",
        header: t("core:full_name"),
        cell: (cell) => (
          <EmployeeListItem employee={cell.row.original.employee} behavior={"card"} />
        ),
        ...ColumnSizes.employee,
      })
    );

    for (const category of report?.config.categories ?? []) {
      inner.push(
        columnHelper.display({
          id: `category|${category.id}`,
          header: category.title,
          cell: (cell) => {
            const policyCell = cell.row.original.cells.find(
              (item) => item.category.id === category.id
            );
            const policy = policyCell?.has_policy ? policyCell.policy : null;
            const openPolicyChecks = () =>
              openModal(TimeOffPolicyChecksModal, {
                employee: cell.row.original.employee,
                category,
              });

            if (!policy) {
              return (
                <Text
                  className={"text-warning underline cursor-pointer"}
                  onClick={openPolicyChecks}
                >
                  {t("time_off:approval_policy_required")}
                </Text>
              );
            }

            const approvalPolicyTitle = policyCell?.approval_policy?.title;
            const isApprovalPolicyRequired =
              policy.accrual_confirm_required && !approvalPolicyTitle;
            const approvalPolicyCaption = isApprovalPolicyRequired
              ? t("time_off:approval_policy_required")
              : (approvalPolicyTitle ?? t("common:none"));

            return (
              <Tooltip>
                <TooltipTrigger asChild>
                  <Text
                    className={"truncate underline cursor-pointer hover:text-brand"}
                    onClick={openPolicyChecks}
                  >
                    {policyCell?.policy_number
                      ? `${policyCell.policy_number}. ${policy.title}`
                      : policy.title}
                  </Text>
                </TooltipTrigger>
                <TooltipContent className={"px-3 py-2"}>
                  <Stack gap={"xs"}>
                    {policyCell?.milestone?.title ? (
                      <Text className={"text-brand-light"}>
                        {t("common:milestone")}: {policyCell.milestone.title}
                      </Text>
                    ) : null}
                    <Stack direction={"horizontal"} gap={"xs"}>
                      <Text className={"text-brand-light"}>{t("approval:approval_policy")}:</Text>
                      <Text
                        className={isApprovalPolicyRequired ? "text-warning" : "text-brand-light"}
                      >
                        {approvalPolicyCaption}
                      </Text>
                    </Stack>
                  </Stack>
                </TooltipContent>
              </Tooltip>
            );
          },
          ...ColumnSizes.status,
        })
      );
    }

    return inner;
  }, [report, openModal]);
  const employeeIdsWithoutApproval = report?.report.employee_ids_without_approval ?? [];
  return (
    <Page.StickyLayout
      toolbar={
        <Stack gap={"md"}>
          {employeeIdsWithoutApproval.length > 0 && (
            <EmptyStateBase
              emoji={null}
              title={t("time_off:policy_issues_found")}
              description={t("plurals:employees_policy_not_found", {
                count: employeeIdsWithoutApproval.length,
              })}
              variant={"primary"}
              primaryButton={
                <Button
                  onClick={() => {
                    openModal(PreviewEmployeesModal, {
                      filter: {
                        page_number: 1,
                        employee_ids: employeeIdsWithoutApproval,
                      },
                      addColumns: [],
                      modalTitle: t("core:employees"),
                    });
                  }}
                >
                  {t("time_off:review_employees")}
                </Button>
              }
            />
          )}
          <Search
            onInput={(e) => {
              setSearchTerm(e.currentTarget.value);
            }}
            type={"text"}
            value={searchTerm}
            placeholder={t("common:search")}
            clearable={true}
            onClear={() => {
              setSearchTerm(undefined);
            }}
          />
        </Stack>
      }
      footer={
        report ? (
          <PaginationBlock
            metadata={report.config.metadata}
            onPageChange={(page) => {
              setSearchParams((searchParams) => {
                searchParams.set("page", page.toString());
                return searchParams;
              });
            }}
          />
        ) : undefined
      }
    >
      <Table
        data={report?.report.rows || []}
        columns={columns}
        showSkeleton={isPending}
        stickyHeader={true}
        stickyFirstColumn={true}
        highlightColumnOnHover={true}
      />
    </Page.StickyLayout>
  );
}
